import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { knowledgeBaseApi, type KnowledgeBaseOwner } from '@/api/knowledgeBase'
import { KnowledgeBaseCategory } from '@/types/knowledgeBase'
import type {
  BrandAssetBackground,
  BrandAssetType,
  BrandTokenConfirmationDto,
  BrandTokenRolesPatchDto,
  DesignSystemImportLogoChoice,
  DesignSystemImportRoleChoice,
  DesignSystemImportPreviewDto,
  KnowledgeBaseEntryViewDto,
} from '@/types/knowledgeBase'
import { tokenNoun, type TokenPatch } from '@/components/console/knowledgeBase/brandGuidelinesModel'
import { getDisplayErrorMessages, parseValidationErrors } from '@/utils/errorHandling'

const CATEGORY = KnowledgeBaseCategory.BrandGuidelines

export interface BrandGuidelinesScope {
  clientCompanyId: string
  /** Absent on the company's own page. */
  workspaceId?: string
  /** Only with a workspace: the page opened from a campaign's knowledge base tab. */
  campaignId?: string
}

export type BrandGuidelinesLoad =
  | { kind: 'loading' }
  | { kind: 'failed'; message: string }
  /** `entry` is null when nothing is saved for the company yet. */
  | { kind: 'ready'; entry: KnowledgeBaseEntryViewDto | null }

/** A refused write. Field errors are keyed by the API's field name, so the page can put each beside its field. */
export type WriteRefusal = { ok: false; status: number; message: string; fieldErrors: Record<string, string> }

/** How a write went. */
export type WriteOutcome = { ok: true } | WriteRefusal

/** What a design system would change, or why it cannot be read. Nothing is saved by asking (ORB-1028). */
export type ImportPreviewOutcome = { ok: true; preview: DesignSystemImportPreviewDto } | WriteRefusal

function refusalOf(response: { error?: string; status: number }, fallback: string): WriteRefusal {
  const fieldErrors: Record<string, string> = {}
  for (const entry of parseValidationErrors({ error: response.error })) {
    if (entry.FieldName && !(entry.FieldName in fieldErrors)) fieldErrors[entry.FieldName] = entry.Message
  }
  return { ok: false, status: response.status, message: getDisplayErrorMessages(response.error, fallback).join(' '), fieldErrors }
}

function outcomeOf(response: { error?: string; status: number }, fallback: string): WriteOutcome {
  return !response.error && response.status < 400 ? { ok: true } : refusalOf(response, fallback)
}

/**
 * Brand Guidelines' reads and writes for its own page in the current app (ORB-904).
 *
 * ⚑ Every write goes to the level the page shows: the company's own page writes the company's, and a workspace's page
 * the workspace's own (ORB-1027). A workspace with no brand of its own gets one on its first write, a copy of the
 * company's that the API makes before applying the write. A campaign's page only reads, through its own level's
 * endpoint, which answers with whatever its workspace uses.
 */
export function useBrandGuidelines({ clientCompanyId, workspaceId, campaignId }: BrandGuidelinesScope) {
  const [load, setLoad] = useState<BrandGuidelinesLoad>({ kind: 'loading' })
  // Bumped by every read, so an older read never lands over a newer one.
  const latest = useRef(0)
  useEffect(() => () => { latest.current++ }, [])

  const owner = useMemo<KnowledgeBaseOwner | null>(
    () => (campaignId ? null : { clientCompanyId, workspaceId }),
    [clientCompanyId, workspaceId, campaignId])

  const reload = useCallback(async () => {
    const read = ++latest.current
    const response = workspaceId && campaignId
      ? await knowledgeBaseApi.getCampaignEntry(clientCompanyId, workspaceId, campaignId, CATEGORY)
      : workspaceId
        ? await knowledgeBaseApi.getWorkspaceEntry(clientCompanyId, workspaceId, CATEGORY)
        : await knowledgeBaseApi.getCompanyEntry(clientCompanyId, CATEGORY)
    if (read !== latest.current) return
    if (response.data) setLoad({ kind: 'ready', entry: response.data })
    else if (response.status === 404) setLoad({ kind: 'ready', entry: null })
    else setLoad({ kind: 'failed', message: response.error || 'Brand Guidelines could not load.' })
  }, [clientCompanyId, workspaceId, campaignId])

  useEffect(() => {
    setLoad({ kind: 'loading' })
    void reload()
  }, [reload])

  /**
   * ⚑ Every write is followed by a read, never by the write's own response: only a read carries the files' download
   * addresses, so taking a write's entry as the page's would blank every logo and file link.
   */
  const thenReload = useCallback(async (response: { error?: string; status: number }, fallback: string): Promise<WriteOutcome> => {
    const outcome = outcomeOf(response, fallback)
    if (outcome.ok) await reload()
    return outcome
  }, [reload])

  /** The level every write goes to. A campaign's page has none, and writing from it is a bug, so it says so. */
  const writer = useCallback((): KnowledgeBaseOwner => {
    if (owner === null) throw new Error("A campaign's Brand Guidelines are read only; they change at its workspace or company.")
    return owner
  }, [owner])

  const patchRoles = useCallback(async (dto: BrandTokenRolesPatchDto) =>
    thenReload(await knowledgeBaseApi.patchBrandTokenRoles(writer(), dto), 'The role could not be saved.'),
  [writer, thenReload])

  /** One of the brand's own entries: a color, spacing step, radius or shadow, a type style, or a font family. */
  const patchEntry = useCallback(async (patch: TokenPatch) => {
    const to = writer()
    const response = patch.kind === 'Style'
      ? await knowledgeBaseApi.patchBrandTypeStyle(to, patch.dto)
      : patch.kind === 'Family'
        ? await knowledgeBaseApi.patchBrandTypeFamily(to, patch.dto)
        : await knowledgeBaseApi.patchBrandToken(to, patch.kind, patch.dto)
    return thenReload(response, `The ${tokenNoun(patch.kind)} could not be ${patch.dto.Remove ? 'removed' : 'saved'}.`)
  }, [writer, thenReload])

  const confirm = useCallback(async (dto: BrandTokenConfirmationDto) =>
    thenReload(await knowledgeBaseApi.confirmBrandTokens(writer(), dto), 'The colors could not be confirmed.'),
  [writer, thenReload])

  // A person's save marks Webster's words as reviewed on the server, so confirming them is saving them unchanged.
  const saveWords = useCallback(async (text: string) => {
    const to = writer()
    const dto = { ClientCompanyId: clientCompanyId, InheritFromParent: false, TextContent: text }
    const response = to.workspaceId
      ? await knowledgeBaseApi.upsertWorkspaceEntry(clientCompanyId, to.workspaceId, CATEGORY, dto)
      : await knowledgeBaseApi.upsertCompanyEntry(clientCompanyId, CATEGORY, dto)
    return thenReload(response, 'The words could not be saved.')
  }, [clientCompanyId, writer, thenReload])

  /** A file, typed when it fills a brand slot, and marked for the background it is made for when it is a mark (ORB-968). */
  const upload = useCallback(async (file: File, assetType?: BrandAssetType, background?: BrandAssetBackground | null) => {
    const to = writer()
    const response = to.workspaceId
      ? await knowledgeBaseApi.uploadWorkspaceFile(clientCompanyId, to.workspaceId, CATEGORY, file, assetType, background)
      : await knowledgeBaseApi.uploadCompanyFile(clientCompanyId, CATEGORY, file, assetType, background)
    return thenReload(response, 'The file could not be uploaded.')
  }, [clientCompanyId, writer, thenReload])

  const deleteFile = useCallback(async (fileId: string) => {
    const to = writer()
    const response = to.workspaceId
      ? await knowledgeBaseApi.deleteWorkspaceFile(clientCompanyId, to.workspaceId, CATEGORY, fileId)
      : await knowledgeBaseApi.deleteCompanyFile(clientCompanyId, CATEGORY, fileId)
    return thenReload(response, 'The file could not be removed.')
  }, [clientCompanyId, writer, thenReload])

  /** Marks a file for light or dark backgrounds, or for either with null (ORB-968). */
  const markFile = useCallback(async (fileId: string, background: BrandAssetBackground | null) =>
    thenReload(await knowledgeBaseApi.patchFileBackground(writer(), CATEGORY, fileId, background), 'The file could not be marked.'),
  [writer, thenReload])

  /** Deletes a workspace's own brand, so it uses the company's again. Only a workspace's page has one to delete. */
  const revertToCompanyBrand = useCallback(async () => {
    const to = writer()
    if (!to.workspaceId) throw new Error("Only a workspace's own Brand Guidelines can give way to the company's.")
    return thenReload(await knowledgeBaseApi.deleteWorkspaceOverride(clientCompanyId, to.workspaceId, CATEGORY), 'The brand could not be removed.')
  }, [clientCompanyId, writer, thenReload])

  // The server always refreshes a single category, which leaves an imported set alone and carries a person's tokens.
  const populate = useCallback(async () =>
    outcomeOf(await knowledgeBaseApi.aiPopulateCategory(writer(), CATEGORY, { OverwriteExisting: true }), 'Webster could not start.'),
  [writer])

  /** Reads a design system's zip and says what importing it would change (ORB-1028). Nothing is saved. */
  const previewImport = useCallback(async (zip: File): Promise<ImportPreviewOutcome> => {
    const response = await knowledgeBaseApi.previewDesignSystemImport(writer(), zip)
    return response.data ? { ok: true, preview: response.data } : refusalOf(response, 'The design system could not be read.')
  }, [writer])

  /** Imports the zip with the person's choice for each logo in it and each role asked, replacing the brand at this level (ORB-1028). */
  const importDesignSystem = useCallback(async (zip: File, logos: DesignSystemImportLogoChoice[], roles: DesignSystemImportRoleChoice[]) =>
    thenReload(await knowledgeBaseApi.importDesignSystem(writer(), zip, logos, roles), 'The design system could not be imported.'),
  [writer, thenReload])

  return {
    load, reload, patchRoles, patchEntry, confirm, saveWords, upload, deleteFile, markFile, revertToCompanyBrand, populate,
    previewImport, importDesignSystem,
  }
}
